/**
 * Settings 状态管理 Store
 *
 * 缓存当前用户的 `/settings` 接口数据（alertEmail / emailSubscribed），
 * 由 `useAuthedBootstrap` 在登录态进入 Layout 时拉取一次，
 * 修改通知邮箱（updateNotificationEmail）/ 邮件订阅开关（updateEmailSubscription）
 * 成功后由调用方触发刷新或乐观更新。
 */

import { create } from 'zustand';
import { getSettings, type UserSettings } from '@/api';

/**
 * Settings 状态接口
 */
export interface SettingsState {
  // ==================== 状态 ====================

  /** 当前用户的告警邮箱；未设置时为空字符串 */
  alertEmail: string;
  /**
   * 是否订阅邮件通知。设置邮箱时后端会自动开启；用户可通过
   * `PUT /settings/email-subscription` 单独控制。
   * 默认 true，避免初次拉取前 UI 误显示「未订阅」。
   */
  emailSubscribed: boolean;
  /** 是否正在请求 /settings */
  isLoadingSettings: boolean;
  /**
   * 是否已成功拉取过一次 /settings。
   * 用于区分「未拉过」与「拉过但邮箱为空」两种状态，
   * 避免后续刷新时整块 UI 回到 skeleton。
   */
  settingsLoaded: boolean;

  // ==================== Actions ====================

  /**
   * 拉取 /settings，更新 alertEmail / emailSubscribed / settingsLoaded。
   */
  refreshSettings: () => Promise<void>;

  /**
   * 直接覆盖 alertEmail（用于 mutation 后的乐观更新）。
   */
  setAlertEmail: (email: string) => void;

  /**
   * 直接覆盖 emailSubscribed（用于 mutation 后的乐观更新）。
   */
  setEmailSubscribed: (subscribed: boolean) => void;

  /**
   * 清空所有 settings 数据；切换账号 / 登出时调用。
   */
  clear: () => void;
}

const initialState = {
  alertEmail: '',
  emailSubscribed: true,
  isLoadingSettings: false,
  settingsLoaded: false,
};

/**
 * Settings Store
 *
 * 仅内存缓存接口数据，刷新页面后必须重新请求，避免展示过期用户设置。
 */
export const useSettingsStore = create<SettingsState>()((set) => ({
  ...initialState,

  refreshSettings: async () => {
    set({ isLoadingSettings: true });
    try {
      const res = await getSettings();
      if (res.ok) {
        const data: UserSettings = res.data;
        set({
          alertEmail: data.alertEmail ?? '',
          // emailSubscribed 是 yaml required 字段，但旧版本后端可能不返回；
          // 缺失时按「订阅」处理，避免误把未订阅状态显示给用户。
          emailSubscribed:
            typeof data.emailSubscribed === 'boolean'
              ? data.emailSubscribed
              : true,
          settingsLoaded: true,
        });
      } else {
        console.error('[Settings Store] getSettings failed:', res.error);
      }
    } catch (error) {
      console.error('[Settings Store] getSettings error:', error);
    } finally {
      set({ isLoadingSettings: false });
    }
  },

  setAlertEmail: (email) => {
    set({ alertEmail: email, settingsLoaded: true });
  },

  setEmailSubscribed: (subscribed) => {
    set({ emailSubscribed: subscribed, settingsLoaded: true });
  },

  clear: () => {
    set(initialState);
  },
}));

/**
 * 便捷 Hooks
 */

/** 当前告警邮箱（未配置时为空字符串） */
export const useAlertEmail = () =>
  useSettingsStore((state) => state.alertEmail);

/** 当前是否订阅邮件通知 */
export const useEmailSubscribed = () =>
  useSettingsStore((state) => state.emailSubscribed);

/** 是否正在请求 /settings */
export const useSettingsLoading = () =>
  useSettingsStore((state) => state.isLoadingSettings);

/** 是否已成功拉取过一次 /settings */
export const useSettingsLoaded = () =>
  useSettingsStore((state) => state.settingsLoaded);

/** 获取 refreshSettings action */
export const useRefreshSettings = () =>
  useSettingsStore((state) => state.refreshSettings);

/** 获取 setAlertEmail action */
export const useSetAlertEmail = () =>
  useSettingsStore((state) => state.setAlertEmail);

/** 获取 setEmailSubscribed action */
export const useSetEmailSubscribed = () =>
  useSettingsStore((state) => state.setEmailSubscribed);
